import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import { components } from "react-select";

import { useFormInput, formatDate } from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import {
    useGetRentCorpDetailQuery,
    useCreateRentCorpMutation,
    useChangeRentCorpInfoMutation,
    useUpdateRentCorpMutation,
} from "@/redux/rentCorpApi";
import { useUploadFileMutation } from "@/redux/uploadApi";
import { useGetBankListQuery } from "@/redux/metaApi";

const CorpTypeOptions = [
    { value: "CORP", label: "법인" },
    { value: "PERSONAL", label: "개인" },
];

const UploadTypeOptions = [
    { value: "BIZ_LIC", label: "사업자등록증" },
    { value: "BANK", label: "통장사본" },
    { value: "CORP_SEAL", label: "법인인감" },
];

const typeNames = {
    BIZ_LIC: "사업자 등록증 파일",
    BANK: "통장 사본 파일",
    CORP_SEAL: "법인 인감 파일",
};

export const BusinessRegister = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,

        // 사업자 필드
        corpName: "",
        corpType: "CORP",
        corpNumber: "",
        bizNumber: "",
        address: "",
        addressDetail: "",
        bCode: "",
        bizStartedAt: "",
        repName: "",
        bizType: "",
        bizCategory: "",
        bankName: "",
        bankAccount: "",
        bankOwner: "",
        rentCorpUploads: [],
        isRepresentative: "N",
        bcode: "",
    });

    useEffect(() => {
        console.log(formData);
    }, [formData]);

    const { mode, toastMsg, confirmStatus, addressPopupStatus } = formData;

    // 1) 모드 설정: id 유무
    useEffect(() => {
        if (id) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: id,
            }));
        }
    }, [id, setFormData]);

    // 2) UPDATE 모드일 경우, 사업자 상세조회
    const {
        data: data,
        isLoading: isDetailLoading,
        error: detailError,
    } = useGetRentCorpDetailQuery(id, {
        skip: !id, // id 없으면 호출X
    });

    // 3) 상세조회 응답 → formData 매핑
    useEffect(() => {
        if (data && data.data) {
            const d = data.data;
            setFormData((prev) => ({
                ...prev,
                corpName: d.corpName || "",
                corpType: d.corpType || "CORP",
                corpNumber: d.corpNumber || "",
                bizNumber: d.bizNumber || "",
                address: d.address || "",
                addressDetail: d.addressDetail || "",
                bCode: d.bCode || "",
                areaCode: d.bCode || "",
                bizStartedAt: d.bizStartedAt || "",
                repName: d.repName || "",
                bizType: d.bizType || "",
                bizCategory: d.bizCategory || "",
                bankName: d.bankName || "",
                bankAccount: d.bankAccount || "",
                bankOwner: d.bankOwner || "",
                rentCorpUploads: d.rentCorpUploads || [],
                bcode: d.bcode || "",
            }));
        }
    }, [data, setFormData]);

    // 5) post / put mutation
    const [createRentCorp, { isLoading: isCreateLoading }] = useCreateRentCorpMutation();
    // const [changeRentCorpInfo, { isLoading: isUpdateLoading }] = useChangeRentCorpInfoMutation();
    const [updateRentCorpInfo, { isLoading: isUpdateLoading }] = useUpdateRentCorpMutation();

    // 6) 저장 Confirm
    const handleSaveConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = {
                corpName: formData.corpName,
                corpType: formData.corpType,
                corpNumber: formData.corpNumber,
                bizNumber: formData.bizNumber,
                address: formData.address,
                addressDetail: formData.addressDetail,
                bCode: formData.bCode,
                bizStartedAt: formData.bizStartedAt,
                repName: formData.repName,
                bizType: formData.bizType,
                bizCategory: formData.bizCategory,
                bankName: formData.bankName,
                bankAccount: formData.bankAccount,
                bankOwner: formData.bankOwner,
                rentCorpUploads: formData.rentCorpUploads,
                bcode: formData.bcode,
            };

            if (mode === "UPDATE") {
                // 수정
                await updateRentCorpInfo({
                    rentCorpId: id,
                    body: requestBody,
                }).unwrap();
            } else {
                // 등록
                await createRentCorp(requestBody).unwrap();
            }

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/business/manage"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    // 7) 저장 버튼
    const handleSave = () => {

        if(!formData.corpName) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자명은 필수입니다.",
            }));
            return;
        }

        if(!formData.corpType) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "법인/개인 사업자 구분은 필수입니다.",
            }));
            return;
        }

        if(!formData.bizNumber) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자번호는 필수입니다.",
            }));
            return;
        }

        if(!formData.repName) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "대표자명은 필수입니다.",
            }));
            return;
        }

        if(!formData.address) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "주소입력은 필수입니다.",
            }));
            return;
        }


        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `사업자 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleSaveConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">사업자 {mode === "CREATE" ? "등록" : "수정"}</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                저장
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">사업자 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <BusinessForm formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">계좌 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <BankInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>

                            {/* 첨부파일 섹션 */}
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">첨부파일</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <UploadFiles formData={formData} setFormData={setFormData} />
                                    </div>
                                    <p style={{ fontSize: "14px", color: "red" }}>
                                        * 인감도장이미지 미등록 시 계약서의 효력이 낮아질수도 있습니다. 이로인한 법적인 책임은 모빌리카와
                                        무관합니다.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {formData.confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={formData.confirmStatus.confirmTitle}
                    description={formData.confirmStatus.confirmDescription}
                    buttons={formData.confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            address: data.address,
                            bCode: data.bcode,
                        }))
                    }
                />
            )}
        </>
    );
};

const BusinessForm = ({ formData, handleChange, setFormData }) => {
    const {
        corpName,
        corpType,
        corpNumber,
        bizNumber,
        address,
        addressDetail,
        bCode,
        bizStartedAt,
        repName,
        bizType,
        bizCategory,
        bankName,
        bankAccount,
        bankOwner,
        rentCorpUploads,
        bcode,
        isRepresentative,
    } = formData;

    // API 호출: 은행리스트 조회
    const { data: bankData, isLoading: bankLoading } = useGetBankListQuery();

    // 은행 Select 예시
    const bankOptions = bankData?.data
        ? bankData.data.map((bank) => ({
              value: bank.code,
              label: bank.name,
              icon: bank.imgUrl,
          }))
        : [];

    // 커스텀 옵션: 드롭다운에서는 이미지와 라벨 모두 표시
    const IconOption = (props) => (
        <components.Option {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} />
            {props.data.label}
        </components.Option>
    );

    // 커스텀 SingleValue: 선택된 값은 라벨만 표시
    const CustomSingleValue = (props) => (
        <components.SingleValue {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} /> {props.data.label}
        </components.SingleValue>
    );

    return (
        <>
            {/* 사업자명 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자명 <em>*</em></span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="corpName" placeholder="사업자명" value={corpName || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 대표사업자 여부 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>대표사업자 여부</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="isRepresentative"
                                id="isRepresentative_Y"
                                checked={isRepresentative === "Y"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        isRepresentative: "Y",
                                    }));
                                }}
                            />
                            <label htmlFor="isRepresentative_Y">활성</label>
                        </div>
                        <div className="radiobox">
                            <input
                                type="radio"
                                name="isRepresentative"
                                id="isRepresentative_N"
                                checked={isRepresentative === "N"}
                                onChange={(e) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        isRepresentative: "N",
                                    }));
                                }}
                            />
                            <label htmlFor="isRepresentative_N">비활성</label>
                        </div>
                    </div>
                </div>
            </div>

            {/* 법인/개인 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>법인/개인<em>*</em></span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={CorpTypeOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            isSearchable={false}
                            placeholder="법인/개인 선택"
                            value={CorpTypeOptions.find((opt) => opt.value === corpType)}
                            onChange={(option) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    corpType: option.value,
                                }));
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* 법인번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>법인번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="corpNumber" placeholder="법인번호" value={corpNumber || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 사업자번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업자번호<em>*</em></span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="bizNumber" placeholder="사업자번호" value={bizNumber || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 대표자명 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>대표자명<em>*</em></span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="repName" placeholder="대표자명" value={repName || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 사업시작일 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업시작일</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="bizStartedAt"
                            value={formatDate(bizStartedAt)}
                            placeholder="ex) 20250101"
                            onChange={handleChange}
                            maxLength={10}
                        />
                    </div>
                </div>
            </div>

            {/* 사업유형 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>사업유형</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="bizType" placeholder="사업유형" value={bizType || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 업종 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>업종</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="bizCategory" placeholder="업종" value={bizCategory || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 주소 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>주소<em>*</em></span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            name="bCode"
                            placeholder=""
                            value={bCode || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                }))
                            }
                        />
                    </div>
                    <div className="form-element" style={{ width: "468px" }}>
                        <input
                            type="text"
                            name="address"
                            placeholder="주소"
                            value={address || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                }))
                            }
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="addressDetail"
                            placeholder="상세 주소를 입력해주세요."
                            value={addressDetail || ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                }))
                            }
                        >
                            주소검색
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

// 계좌 정보 컴포넌트
const BankInfo = ({ formData, handleChange, setFormData }) => {
    const { bankName, bankAccount, bankOwner } = formData;

    // API 호출: 은행리스트 조회
    const { data: bankData, isLoading: bankLoading } = useGetBankListQuery();

    // 은행 Select 예시
    const bankOptions = bankData?.data
        ? bankData.data.map((bank) => ({
              value: bank.code,
              label: bank.name,
              icon: bank.imgUrl,
          }))
        : [];

    // 커스텀 옵션: 드롭다운에서는 이미지와 라벨 모두 표시
    const IconOption = (props) => (
        <components.Option {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} />
            {props.data.label}
        </components.Option>
    );

    // 커스텀 SingleValue: 선택된 값은 라벨만 표시
    const CustomSingleValue = (props) => (
        <components.SingleValue {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} /> {props.data.label}
        </components.SingleValue>
    );

    return (
        <>
            {/* 예금주 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>예금주</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="bankOwner"
                            placeholder="예금주를 입력하세요"
                            value={bankOwner || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>

            {/* 은행 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>은행</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <Select
                            options={bankOptions}
                            className="custom-select"
                            classNamePrefix="custom-select"
                            placeholder="은행 선택"
                            value={bankOptions.find((opt) => opt.value === bankName)}
                            onChange={(option) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    bankName: option.value,
                                }))
                            }
                            components={{ Option: IconOption, SingleValue: CustomSingleValue }}
                        />
                    </div>
                </div>
            </div>

            {/* 계좌번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>계좌번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="bankAccount"
                            placeholder="계좌번호를 입력하세요"
                            value={bankAccount || ""}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

const UploadFiles = ({ formData, setFormData }) => {
    const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();

    // 각 파일 타입별로 별도의 ref 생성
    const bizLicFileRef = useRef(null);
    const bankFileRef = useRef(null);
    const corpSealFileRef = useRef(null);

    const handleFileChange = async (e, uploadType) => {
        const fileObj = e.target.files[0];
        if (!fileObj) return;

        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const formDataObj = new FormData();
            formDataObj.append("file", fileObj);

            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;

            setFormData((prev) => {
                const updated = [...(prev.rentCorpUploads || [])];
                const idx = updated.findIndex((x) => x.uploadType === uploadType);
                if (idx >= 0) {
                    updated[idx] = { ...updated[idx], uploadId: newUploadId, fileName: fileObj.name, upload: res.data };
                } else {
                    updated.push({ uploadId: newUploadId, uploadType, fileName: fileObj.name, upload: res.data });
                }
                return {
                    ...prev,
                    rentCorpUploads: updated,
                };
            });
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        } finally {
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
            }));
        }
    };

    const handleButtonClick = (type) => {
        switch (type) {
            case "BIZ_LIC":
                bizLicFileRef.current.click();
                break;
            case "BANK":
                bankFileRef.current.click();
                break;
            case "CORP_SEAL":
                corpSealFileRef.current.click();
                break;
            default:
                break;
        }
    };

    const getFileName = (uploadType) => {
        const uploadItem = formData?.rentCorpUploads?.find((item) => item.uploadType === uploadType);
        return uploadItem?.upload?.originFileName || "";
    };

    return (
        <>
            {/* 사업자등록증 */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>사업자등록증</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName("BIZ_LIC")}
                            placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                            onClick={() => {
                                const uploadItem = formData?.rentCorpUploads?.find((item) => item.uploadType === "BIZ_LIC");
                                if (uploadItem && uploadItem.upload && uploadItem.upload.url) {
                                    window.open(uploadItem.upload.url, "_blank");
                                } else {
                                    handleButtonClick("BIZ_LIC");
                                }
                            }}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={() => handleButtonClick("BIZ_LIC")}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={bizLicFileRef}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.png,.jpeg"
                            onChange={(e) => handleFileChange(e, "BIZ_LIC")}
                        />
                    </div>
                </div>
            </div>

            {/* 통장사본 */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>통장사본</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName("BANK")}
                            placeholder="4MB 이하 PDF 파일로 첨부해주세요."
                            onClick={() => {
                                const uploadItem = formData?.rentCorpUploads?.find((item) => item.uploadType === "BANK");
                                if (uploadItem && uploadItem.upload && uploadItem.upload.url) {
                                    window.open(uploadItem.upload.url, "_blank");
                                } else {
                                    handleButtonClick("BANK");
                                }
                            }}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={() => handleButtonClick("BANK")}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={bankFileRef}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.png,.jpeg"
                            onChange={(e) => handleFileChange(e, "BANK")}
                        />
                    </div>
                </div>
            </div>

            {/* 법인인감 */}
            <div className="item-row" data-grow="2">
                <div className="row-head">
                    <span>법인인감</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            readOnly
                            value={getFileName("CORP_SEAL")}
                            placeholder="4MB 이하 PNG 파일로 첨부해주세요."
                            onClick={() => {
                                const uploadItem = formData?.rentCorpUploads?.find((item) => item.uploadType === "CORP_SEAL");
                                if (uploadItem && uploadItem.upload && uploadItem.upload.url) {
                                    window.open(uploadItem.upload.url, "_blank");
                                } else {
                                    handleButtonClick("CORP_SEAL");
                                }
                            }}
                        />
                    </div>
                    <div className="form-element_button">
                        <button type="button" className="btn-form_element btn-outline" onClick={() => handleButtonClick("CORP_SEAL")}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={corpSealFileRef}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.png,.jpeg"
                            onChange={(e) => handleFileChange(e, "CORP_SEAL")}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="2"></div>
        </>
    );
};
